<template>
    <!-- Header: icon + title + pill -->
    <header
        class="flex items-start gap-3"
        :class="{ 'opacity-50': locked }"
    >
        <BenefitIcon
            :category="row.item.benefit?.category"
            :title="row.item.benefit?.benefit_title"
            :description="row.item.benefit?.benefit_description"
            svg-class="w-6 h-6 text-slate-600 dark:text-slate-300"
            class="shrink-0 w-12 h-12"
        />
        <div class="min-w-0 flex-1">
            <h3
                class="text-slate-800 dark:text-slate-100 font-medium text-sm leading-5 line-clamp-1"
            >
                {{ row.name || 'Unknown Benefit' }}
            </h3>
            <div
                v-if="row.value_up_to !== '—'"
                class="text-slate-500 dark:text-slate-400 text-sm py-1 px-2 bg-slate-100 dark:bg-slate-700 rounded-lg max-w-fit font-medium mt-1"
            >
                Up to {{ row.value_up_to }}
            </div>
        </div>
    </header>

    <!-- Usage block (dimmed when locked, mirroring the Current tab pattern) -->
    <div
        class="mt-4 flex flex-col gap-2"
        :class="{ 'opacity-50': locked }"
    >
        <div class="flex items-center justify-between gap-2">
            <span class="text-slate-800 dark:text-slate-100 text-sm">
                {{ formatMoney(row.used) }} of
                {{ formatMoney(row.total) }}
            </span>
            <div
                class="flex items-center gap-2 text-slate-500 dark:text-slate-400 text-sm whitespace-nowrap shrink-0"
            >
                <span v-if="row.expiration_time">
                    {{ row.expiration_time }}
                </span>
                <span class="inline-flex items-center gap-1">
                    <Icon icon="arrows-repeat" class="w-4 h-4" />
                    {{ row.frequency }}
                </span>
            </div>
        </div>
        <div class="bg-orange-200 rounded-full h-1 relative">
            <div
                class="bg-10x-orange h-1 rounded-full transition-all"
                :style="{ width: clamp(row.utilization) + '%' }"
            ></div>
            <div
                v-if="Number(row.utilization) >= 100"
                class="absolute -right-1 top-1/2 -translate-y-1/2 w-5 h-5 bg-10x-orange rounded-full flex items-center justify-center border-2 border-white"
            >
                <Icon
                    icon="check"
                    svgClass="w-2 h-2 text-white"
                    class="text-white"
                />
            </div>
        </div>
        <div v-if="!hideUtilization" class="text-slate-500 dark:text-slate-400 text-sm">
            Unused {{ formatMoney(row.unused) }} · Utilization
            {{ row.utilization }}%
        </div>
    </div>

    <!-- Card image + name -->
    <div
        v-if="showCard"
        class="flex items-center gap-2 mt-4 min-w-0"
        :class="locked && 'opacity-50'"
    >
        <ImageLoader class="shrink-0">
            <template #image>
                <img
                    v-if="
                        row.item.card?.image?.path &&
                        row.item.card?.image?.file_name
                    "
                    :src="`${cdnUrl}/${row.item.card.image.path}/_small/${row.item.card.image.file_name}`"
                    :alt="row.card_name"
                    class="w-8 h-5 rounded-sm"
                />
            </template>
            <template #error>
                <Icon icon="credit-card" class="w-8 h-5" />
            </template>
        </ImageLoader>
        <div class="flex items-center min-w-0 text-sm text-slate-800 dark:text-slate-100">
            <span class="truncate font-medium mr-1">
                {{ row.card_name || 'Unknown Card' }}
            </span>
            <span
                v-if="insightsShowCardNumbers && row.card_last_4"
                class="text-slate-500 dark:text-slate-400 text-xs shrink-0 whitespace-nowrap"
            >
                ({{ row.card_last_4 }})
            </span>
        </div>
    </div>

    <!-- User row (only when wallet has multiple users) -->
    <div
        v-if="hasMultipleUsers && row.item.user?.name"
        class="flex items-center gap-2 mt-2"
        :class="locked && 'opacity-50'"
    >
        <div
            class="flex h-5 w-5 p-1 justify-center items-center rounded-full bg-slate-100 dark:bg-slate-700"
        >
            <Icon
                :icon="row.item.user.is_business ? 'business' : 'user'"
                class="w-3 h-3 text-slate-500 dark:text-slate-400"
            />
        </div>
        <span class="text-slate-800 dark:text-slate-100 text-sm">
            {{ row.item.user.name }}
        </span>
    </div>

    <!-- Locked state: full-width upgrade button at the bottom (matches Current) -->
    <button
        v-if="locked"
        type="button"
        class="flex w-full items-center justify-center gap-2 mt-4 py-3 px-4 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 text-sm font-medium hover:bg-slate-50 dark:hover:bg-slate-700 cursor-pointer"
        @click.stop="openUpgrade"
    >
        <Icon icon="lock" class="w-4 h-4 text-slate-500 dark:text-slate-400" />
        <span>Upgrade to track</span>
    </button>
</template>

<script setup>
import { computed } from 'vue';
import { storeToRefs } from 'pinia';
import { useCardBenefitsStore } from '@/store/card-benefits';
import BenefitIcon from '@/components/card-benefits/benefit-icon.vue';
import ImageLoader from '@/components/ui/image-loader/image-loader.vue';
import Icon from '@/components/ui/icon/icon.vue';

defineProps({
    row: { type: Object, required: true },
    locked: { type: Boolean, default: false },
    showCard: { type: Boolean, default: true },
    hideUtilization: { type: Boolean, default: false },
});

const store = useCardBenefitsStore();
const { insightsShowCardNumbers } = storeToRefs(store);
const cdnUrl = computed(() => store.cdnUrl);
const openUpgrade = () => store.setShowUpgradeModal(true);
const hasMultipleUsers = computed(
    () => (store.insightsUserOptions || []).length > 1
);

const clamp = (n) => Math.max(0, Math.min(100, Number(n) || 0));
const formatMoney = (value) => {
    if (!value && value !== 0) return '$0';
    return '$' + Math.round(Number(value)).toLocaleString('en-US');
};
</script>
